import React, { useState, useEffect } from 'react';
import { ChevronLeft, Share2, ShoppingCart, Minus, Plus, Check, X } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../components/CartContext';
// import { productsData } from './Shop';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedImage, setSelectedImage] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get the product from productsData using the id from URL
  // const product = productsData[parseInt(id)];

  useEffect(() => {
    async function loadProduct() {
      try {
        const base = process.env.REACT_APP_API_BASE; // CRA
        const res = await fetch(`${base}/api/products/${id}`, {
          credentials: "include",
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const p = await res.json(); // backend returns the product object

        // normalize to match your UI expectations
        const normalized = {
          id: p.id,
          name: p.name,
          price: Number(p.price),
          description: p.description || "",
          images: (p.images && p.images.length ? p.images : [p.image]).filter(Boolean),
          sizes: Array.isArray(p.sizes) ? p.sizes : [],
        };

        setProduct(normalized);
        setIsVisible(true);
      } catch (e) {
        console.error("Failed to load product:", e);
        navigate("/shop");
      } finally {
        setLoading(false);
      }
    }

    loadProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

  if (loading) return <div className="p-8">Loading...</div>;
  if (!product) return null;


  // // If product not found, redirect to shop
  // useEffect(() => {
  //   if (!product) {
  //     navigate('/shop');
  //   } else {
  //     setIsVisible(true);
  //   }
  // }, [product, navigate]);

  // // Prevent body scroll when modal is open
  // useEffect(() => {
  //   if (showSizeGuide) {
  //     document.body.style.overflow = 'hidden';
  //   } else {
  //     document.body.style.overflow = 'unset';
  //   }
  //   return () => {
  //     document.body.style.overflow = 'unset';
  //   };
  // }, [showSizeGuide]);

  // // Don't render if product doesn't exist
  // if (!product) {
  //   return null;
  // }

  const handleAddToCart = (buyNow = false) => {
    if (product.sizes.length > 0 && !selectedSize) {
      alert('Please select a size');
      return;
    }
    
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      size: selectedSize,
      quantity: quantity,
      image: product.images[0]
    });
    
    if (buyNow) { navigate('/checkout'); return; }
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const handleShareLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const sizeData = [
    { size: 'XS', chest: '18"', bodyLength: '26"', shoulder: '16"', sleeves: '7"' },
    { size: 'S', chest: '19"', bodyLength: '27"', shoulder: '17"', sleeves: '7.5"' },
    { size: 'M', chest: '20"', bodyLength: '28"', shoulder: '18"', sleeves: '8"' },
    { size: 'L', chest: '21"', bodyLength: '29"', shoulder: '19"', sleeves: '8.5"' },
    { size: 'XL', chest: '22"', bodyLength: '30"', shoulder: '20"', sleeves: '9"' },
    { size: '2XL', chest: '23"', bodyLength: '31"', shoulder: '21"', sleeves: '9.5"' },
    { size: '3XL', chest: '24"', bodyLength: '31.5"', shoulder: '22"', sleeves: '10"' },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#faf8f5' }}>
      {/* Navbar placeholder */}
      <div className="h-4" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Back Button */}
        <button 
          className={`flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-all duration-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
          onClick={() => navigate('/shop')}
        >
          <ChevronLeft size={20} />
          <span>Back to Shop</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div className={`transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* Main Image */}
            <div className="relative overflow-hidden rounded-2xl bg-white shadow-2xl mb-4 group">
              <div className="aspect-square">
                <img
                  src={product.images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>
              
              {/* Share Button */}
              <div className="absolute top-4 right-4">
                <button 
                  onClick={handleShareLink}
                  className={`p-3 rounded-full backdrop-blur-md transition-all duration-300 shadow-lg ${
                    linkCopied ? 'bg-green-500 text-white' : 'bg-white/80 text-gray-700 hover:bg-white'
                  }`}
                >
                  {linkCopied ? <Check size={20} /> : <Share2 size={20} />}
                </button>
              </div>
            </div>

            {/* Thumbnail Gallery */}
            <div className="grid grid-cols-4 gap-4">
              {product.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImage(idx)}
                  className={`relative overflow-hidden rounded-xl transition-all duration-300 ${
                    selectedImage === idx 
                      ? 'ring-4 ring-blue-500 scale-105 shadow-lg' 
                      : 'hover:scale-105 shadow-md'
                  }`}
                  style={{
                    animationDelay: `${idx * 100}ms`,
                    animation: isVisible ? 'fadeInUp 0.6s ease-out forwards' : 'none',
                    opacity: isVisible ? 1 : 0
                  }}
                >
                  <div className="aspect-square bg-white">
                    <img
                      src={img}
                      alt={`View ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div className={`transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">{product.name}</h1>
              <div className="flex items-center gap-4 mb-6">
                <span className="text-3xl font-bold text-blue-600">${product.price}</span>
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">In Stock</span>
              </div>

              <p className="text-gray-600 mb-8 leading-relaxed">{product.description}</p>

              {/* Size Selection */}
              {product.sizes.length > 0 && (
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-semibold text-gray-900">
                    Size:
                  </label>
                  <button 
                    onClick={() => setShowSizeGuide(true)}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium underline"
                  >
                    Size guide
                  </button>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`py-3 rounded-xl font-semibold transition-all duration-300 text-sm ${
                        selectedSize === size
                          ? 'bg-blue-600 text-white shadow-lg scale-105'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-105'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>)}
              

              {/* Quantity Selector */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Quantity:
                </label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-300 hover:scale-110"
                  >
                    <Minus size={20} />
                  </button>
                  <input
                    type="text"
                    value={quantity}
                    readOnly
                    className="w-20 text-center text-xl font-bold py-3 border-2 border-gray-200 rounded-xl"
                  />
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-300 hover:scale-110"
                  >
                    <Plus size={20} />
                  </button>
                </div>
              </div>

              {/* Add to Cart Button */}
              
              <button
                onClick={() => handleAddToCart()}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-500 transform hover:scale-105 shadow-xl ${
                  addedToCart
                    ? 'bg-green-500 text-white'
                    : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800'
                }`}
              >
                {addedToCart ? (
                  <span className="flex items-center justify-center gap-2">
                    <Check size={24} />
                    Added to Cart!
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <ShoppingCart size={24} />
                    Add to cart
                  </span>
                )}
              </button>
              <button onClick={() => handleAddToCart(true)} className="w-full mt-3 py-4 border-2 border-blue-600 text-blue-600 rounded-xl font-bold hover:bg-blue-50 transition-colors">Buy now</button>
              <button onClick={() => navigate('/cart')} className="w-full mt-3 py-2 underline text-gray-600">View cart</button>
            </div>
          </div>
        </div>
      </div>


      {/* Size Guide Modal - Positioned in center below navbar */}
      {showSizeGuide && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
            style={{ top: '64px' }}
            onClick={() => setShowSizeGuide(false)}
          />
          
          {/* Modal */}
          <div 
            className="fixed z-50 flex items-center justify-center p-4" 
            style={{ 
              top: '64px',
              left: 0,
              right: 0,
              bottom: 0
            }}
          >
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[calc(100vh-80px)] overflow-hidden animate-fadeIn">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">Size Guide</h2>
                <button
                  onClick={() => setShowSizeGuide(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={24} className="text-gray-600" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-auto max-h-[calc(100vh-160px)]">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-6 py-4 text-left font-semibold text-gray-900 border border-gray-200">
                          Size
                        </th>
                        <th className="px-6 py-4 text-left font-semibold text-gray-900 border border-gray-200">
                          Chest
                        </th>
                        <th className="px-6 py-4 text-left font-semibold text-gray-900 border border-gray-200">
                          Body Length
                        </th>
                        <th className="px-6 py-4 text-left font-semibold text-gray-900 border border-gray-200">
                          Shoulder To Shoulder
                        </th>
                        <th className="px-6 py-4 text-left font-semibold text-gray-900 border border-gray-200">
                          Sleeves Length
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sizeData.map((row, index) => (
                        <tr 
                          key={row.size}
                          className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                        >
                          <td className="px-6 py-4 font-medium text-gray-900 border border-gray-200">
                            {row.size}
                          </td>
                          <td className="px-6 py-4 text-gray-700 border border-gray-200">
                            {row.chest}
                          </td>
                          <td className="px-6 py-4 text-gray-700 border border-gray-200">
                            {row.bodyLength}
                          </td>
                          <td className="px-6 py-4 text-gray-700 border border-gray-200">
                            {row.shoulder}
                          </td>
                          <td className="px-6 py-4 text-gray-700 border border-gray-200">
                            {row.sleeves}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Measurement Tips */}
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">How to Measure:</h3>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li>• <strong>Chest:</strong> Measure around the fullest part of your chest</li>
                    <li>• <strong>Body Length:</strong> Measure from the highest point of the shoulder to the bottom hem</li>
                    <li>• <strong>Shoulder:</strong> Measure from shoulder point to shoulder point across the back</li>
                    <li>• <strong>Sleeves:</strong> Measure from the shoulder seam to the end of the sleeve</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default ProductDetail;